import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import "../style/initialPage.css";
import { globalT } from "../utils/globalUtils.js";
import {
  blocksThirdPartyCookiesHere,
  buildUnedAuthUrl,
  isEmbeddedBrowser,
  lacksUnedTrustAnchor,
  probeUnedReachable,
  readClientInfo,
} from "../utils/unedAuth.js";

const EDUBOT_IFRAME_ALLOW =
  "fullscreen; geolocation; autoplay; encrypted-media; payment; accelerometer; gyroscope; magnetometer; camera; microphone";
// Margen para que el iframe se anuncie por postMessage. El contador arranca al
// montar y se reinicia, más corto, cuando el documento del iframe ha cargado:
// en red móvil la carga sola ya se comía el presupuesto anterior de 2,5 s y
// dábamos por "no autenticado" a quien sí lo estaba.
const EDUBOT_READY_TIMEOUT_MS = 8000;
const EDUBOT_READY_AFTER_LOAD_MS = 3000;
// Ancho mínimo, en px, con el que el widget de la UNED se maqueta sin recortar
// el resultado (medido: por debajo, la tabla de la corrección desborda). Su CSS
// no es nuestro, así que por debajo de ese ancho el iframe se pinta a este
// tamaño y se reduce proporcionalmente para que quepa.
const EDUBOT_MIN_WIDTH_PX = 400;
// Margen para dar por descartada la navegación al SSO. `pagehide` solo salta
// cuando la página nueva confirma, así que con una conexión lenta la navegación
// puede seguir en curso: hasta agotarlo solo se dice que se está abriendo.
const AUTH_NAV_TIMEOUT_MS = 6000;

const GenreCheckerDropdownPage = (props) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedGenreId, setSelectedGenreId] = useState("");
  const [isEdubotReady, setIsEdubotReady] = useState(false);
  const [showAuthNotice, setShowAuthNotice] = useState(false);
  const [showDemoPreview, setShowDemoPreview] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  // Altura que pide el widget por postMessage (chatRedimensionado).
  const [edubotHeight, setEdubotHeight] = useState(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const frameRef = useRef(null);
  const authTimeoutRef = useRef(null);
  // El navegador embebido descartó la navegación al SSO sin decir nada.
  // null | "opening" (se pulsó y seguimos aquí) | "failed" (se agotó el margen)
  const [authNavState, setAuthNavState] = useState(null);
  const authNavTimeoutRef = useRef(null);
  // Resultado de la sonda de alcance: true | false | null (no concluyente).
  const [unedReachable, setUnedReachable] = useState(null);
  const appliedGenreRef = useRef(false);

  const selectedGenre = useMemo(
    () => props.data.find((item) => item.id === selectedGenreId),
    [props.data, selectedGenreId]
  );
  const selectedIframe = selectedGenre?.checkerIframe;
  const currentIframeClass = selectedIframe?.className;

  const isEnglish = i18n.language?.startsWith("en");
  const embedded = isEmbeddedBrowser();
  // WebKit (Safari y todo iOS) bloquea las cookies de terceros, así que la
  // sesión de la UNED no entra en el iframe por muy bien que vaya el login.
  // Solo ahí se ofrece además abrir el corrector como página propia.
  const iframeSessionBlocked = blocksThirdPartyCookiesHere();
  // El User-Agent ya trae la versión de la app y la de Android, así que el
  // aviso puede decir qué falla sin pedirle capturas a nadie.
  const clientInfo = useMemo(
    () =>
      readClientInfo(
        typeof navigator !== "undefined" ? navigator.userAgent : ""
      ),
    []
  );
  const trustAnchorMissing = useMemo(
    () => lacksUnedTrustAnchor(clientInfo),
    [clientInfo]
  );
  const noticeCopy = isEnglish
    ? {
        loading: "Checking your UNED session...",
        info: embedded
          ? "To use the corrector you need to sign in to UNED from here: tap \"Log in to UNED\". Once you have signed in you will be brought back to this screen (if not, use \"Back to Rewrite\" or the Back button). If the app shows an error on the way back, close it and open it again: your session is already active."
          : "To use the corrector you need to sign in to UNED: tap \"Log in to UNED\" and it opens in a new tab. Once you have signed in, come back to this tab and tap \"I've already signed in\".",
        loginButton: "Log in to UNED",
        retryButton: "I've already signed in",
        exampleButton: "See example",
        webkitInfo: embedded
          ? "On iPhone and iPad the browser blocks your UNED session inside this box, so the corrector will keep asking you to sign in even after you have. Sign in above first, then open it on UNED's own page, where it works normally. To come back, use \"\u2039 Volver a Rewrite\" at the top."
          : "Safari blocks your UNED session inside this box, so the corrector will keep asking you to sign in even after you have. Sign in above first, then open it on UNED's own page, where it works normally. It opens in a new tab, so this one stays as it is.",
        webkitButton: "Open the corrector on UNED",
        navOpening:
          "Opening UNED sign-in… If your connection is slow it may take a few seconds; please wait without tapping again.",
        navFailedTitle:
          "The UNED sign-in page has not opened. If your connection is slow it may still be loading, so give it a few more seconds. If it never opens, update RE-Write from Google Play and try again.",
        navFailedSlow:
          "UNED's server is responding, so the page is probably still on its way. Give it a few more seconds and, if nothing happens, tap \"Try again\".",
        navFailedOutdated:
          "Your version of RE-Write does not recognise UNED's security certificate, which is why the sign-in page never opens. Update RE-Write from Google Play to version 1.0.4 or later and it will work.",
        navFailedOutdatedUnknown:
          "This app does not recognise UNED's security certificate, which is why the sign-in page never opens. If you are using RE-Write, update it from Google Play to version 1.0.4 or later.",
        navFailedNetwork:
          "UNED's server cannot be reached from this connection. Try again on mobile data or on a different Wi-Fi network.",
        navFailedHint:
          "In the meantime you can use the corrector from your browser: open rewrite.es in Chrome and sign in there, doing the whole process in the browser. Signing in with Chrome and coming back to the app will not work, as the session is not shared.",
        navFailedRetry: "Try again",
        demoBadge: "Example — demo preview",
        demoIntro: "This is how the AI autocorrector works once you are signed in to UNED:",
        demoInputLabel: "Your text",
        demoInputText:
          "Yesterday I go to the museum with my friends. We seen many interesting paintings and I take a lot of photos. It was a very nice day and we enjoy a lot.",
        demoFeedbackLabel: "AI feedback",
        demoFeedbackText:
          "Use the past tense consistently: \"I went to the museum\", \"We saw many interesting paintings\", \"I took a lot of photos\" and \"we enjoyed it a lot\". Great use of connectors — try adding a closing sentence to round off the text.",
        demoNote:
          "Demonstration view with sample content. The real correction is generated by UNED's AI after you sign in.",
        demoClose: "Close example",
      }
    : {
        loading: "Comprobando tu sesión de UNED...",
        info: embedded
          ? "Para usar el autocorrector necesitas iniciar sesión en la UNED desde aquí: pulsa \"Autenticar en UNED\". Al terminar volverás a esta pantalla (si no, usa \"Volver a Rewrite\" o el botón Atrás). Si la app muestra un error al volver, ciérrala y ábrela de nuevo: la sesión ya queda iniciada."
          : "Para usar el autocorrector necesitas iniciar sesión en la UNED: pulsa \"Autenticar en UNED\" y se abrirá en una pestaña nueva. Cuando hayas iniciado sesión, vuelve a esta pestaña y pulsa \"Ya me he autenticado\".",
        loginButton: "Autenticar en UNED",
        retryButton: "Ya me he autenticado",
        exampleButton: "Ver ejemplo",
        webkitInfo: embedded
          ? "En iPhone y iPad el navegador bloquea la sesión de la UNED dentro de este recuadro, así que el corrector seguirá pidiéndote autenticarte aunque ya lo hayas hecho. Autentícate arriba y luego ábrelo en la página de la UNED, donde funciona con normalidad. Para volver, usa \"\u2039 Volver a Rewrite\" de arriba."
          : "Safari bloquea la sesión de la UNED dentro de este recuadro, así que el corrector seguirá pidiéndote autenticarte aunque ya lo hayas hecho. Autentícate arriba y luego ábrelo en la página de la UNED, donde funciona con normalidad. Se abre en una pestaña nueva, así que esta se queda como está.",
        webkitButton: "Abrir corrector en la UNED",
        navOpening:
          "Abriendo el acceso de la UNED… Si tu conexión va lenta puede tardar unos segundos; espera sin volver a pulsar.",
        navFailedTitle:
          "El acceso de la UNED no se ha abierto. Si tu conexión va lenta puede que siga cargando, así que dale unos segundos más. Si no llega a abrirse, actualiza RE-Write desde Google Play y vuelve a intentarlo.",
        navFailedSlow:
          "El servidor de la UNED sí responde, así que lo más probable es que la página siga en camino. Dale unos segundos más y, si no pasa nada, pulsa \"Reintentar\".",
        navFailedOutdated:
          "Tu versión de RE-Write no reconoce el certificado de seguridad de la UNED, y por eso el acceso no llega a abrirse. Actualiza RE-Write desde Google Play a la versión 1.0.4 o posterior y funcionará.",
        navFailedOutdatedUnknown:
          "Esta aplicación no reconoce el certificado de seguridad de la UNED, y por eso el acceso no llega a abrirse. Si estás usando RE-Write, actualízala desde Google Play a la versión 1.0.4 o posterior.",
        navFailedNetwork:
          "No se llega al servidor de la UNED desde esta conexión. Prueba con datos móviles o con otra red wifi.",
        navFailedHint:
          "Mientras tanto puedes usar el autocorrector desde el navegador: abre rewrite.es en Chrome e inicia sesión allí, haciendo todo el proceso en el navegador. Iniciar sesión en Chrome y volver a la app no sirve, porque la sesión no se comparte.",
        navFailedRetry: "Reintentar",
        demoBadge: "Ejemplo — vista de demostración",
        demoIntro: "Así funciona el autocorrector de IA una vez has iniciado sesión en UNED:",
        demoInputLabel: "Tu texto",
        demoInputText:
          "Yesterday I go to the museum with my friends. We seen many interesting paintings and I take a lot of photos. It was a very nice day and we enjoy a lot.",
        demoFeedbackLabel: "Corrección de la IA",
        demoFeedbackText:
          "Usa el pasado de forma consistente: \"I went to the museum\", \"We saw many interesting paintings\", \"I took a lot of photos\" y \"we enjoyed it a lot\". Buen uso de los conectores; añade una frase de cierre para rematar el texto.",
        demoNote:
          "Vista de demostración con contenido de ejemplo. La corrección real la genera la IA de la UNED tras iniciar sesión.",
        demoClose: "Cerrar ejemplo",
      };

  const edubotIframeClasses = useMemo(
    () =>
      new Set(
        props.data
          .map((item) => item.checkerIframe?.className)
          .filter(Boolean)
      ),
    [props.data]
  );

  const clearAuthTimeout = () => {
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }
  };

  const armAuthNotice = (delayMs) => {
    clearAuthTimeout();
    authTimeoutRef.current = setTimeout(() => {
      setShowAuthNotice(true);
    }, delayMs);
  };

  const startEdubotCheck = () => {
    setIsEdubotReady(false);
    setShowAuthNotice(false);
    setShowDemoPreview(false);
    armAuthNotice(EDUBOT_READY_TIMEOUT_MS);
  };

  // No se hace preventDefault: la navegación normal del enlace sigue su curso y,
  // si algo de esto fallara, el enlace seguiría siendo un enlace. Solo se arma un
  // temporizador; que llegue a disparar significa que seguimos aquí, es decir,
  // que el navegador embebido se tragó la navegación sin avisar.
  const handleAuthLinkClick = () => {
    // En un navegador real el enlace abre una pestaña nueva y esta página se
    // queda viva a propósito: no hay nada que detectar, y armar el contador
    // pintaba el aviso de fallo aunque el SSO hubiera abierto sin problemas.
    if (!embedded) return;

    clearTimeout(authNavTimeoutRef.current);
    // Confirma al usuario que el toque ha llegado, aunque el SSO tarde.
    setAuthNavState("opening");
    // La sonda va en paralelo a la navegación, no después: si el enlace
    // funciona la página se descarga y da igual, y si no, a los 6 s el
    // veredicto ya está listo y el aviso no hace esperar de más.
    setUnedReachable(null);
    probeUnedReachable().then(setUnedReachable, () => setUnedReachable(null));
    authNavTimeoutRef.current = setTimeout(
      () => setAuthNavState("failed"),
      AUTH_NAV_TIMEOUT_MS
    );
  };

  // Qué decirle a quien ve el aviso. La sonda manda: mide el fallo real en vez
  // de deducirlo. La versión solo sirve para dar el remedio correcto.
  const authFailureCopy = () => {
    if (unedReachable === true) return noticeCopy.navFailedSlow;
    if (unedReachable === false) {
      if (!trustAnchorMissing) return noticeCopy.navFailedNetwork;
      return clientInfo.appVersion
        ? noticeCopy.navFailedOutdated
        : noticeCopy.navFailedOutdatedUnknown;
    }
    // Sonda no concluyente (red lenta o sin fetch): no se acusa a nadie.
    return noticeCopy.navFailedTitle;
  };

  // Identifica al cliente en el propio aviso: una captura del usuario basta
  // para saber con qué está, sin otra ronda de correos.
  const clientLabel = [
    clientInfo.appVersion ? `RE-Write ${clientInfo.appVersion.join(".")}` : null,
    clientInfo.androidMajor ? `Android ${clientInfo.androidMajor}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // `location.assign` es un camino distinto al del enlace y a veces pasa donde
  // el otro no.
  const retryAuthNavigation = () => {
    handleAuthLinkClick();
    window.location.assign(unedAuthUrl);
  };

  const retryEdubotCheck = () => {
    if (!selectedIframe) {
      return;
    }

    startEdubotCheck();
    setIframeKey((prev) => prev + 1);
  };

  useEffect(() => {
    props.setInit(false);
  }, [props]);

  useEffect(() => {
    const cancel = () => clearTimeout(authNavTimeoutRef.current);
    // `pagehide` cubre también la vuelta con Atrás desde el SSO (bfcache).
    window.addEventListener("pagehide", cancel);
    return () => {
      cancel();
      window.removeEventListener("pagehide", cancel);
    };
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setFrameWidth(entry.contentRect.width);
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [selectedIframe]);

  // Al volver del SSO de la UNED llegamos con `?genre=<id>`: recuperamos la
  // selección para que el usuario no aterrice en un desplegable vacío y crea
  // que el login ha fallado. Se aplica una sola vez; a partir de ahí manda él.
  const requestedGenreId = searchParams.get("genre");
  useEffect(() => {
    if (appliedGenreRef.current || !requestedGenreId) {
      return;
    }
    if (!props.data.some((item) => item.id === requestedGenreId)) {
      return;
    }
    appliedGenreRef.current = true;
    setSelectedGenreId(requestedGenreId);
  }, [requestedGenreId, props.data]);

  // Geometría del iframe: altura pedida por el widget (o la inicial) y factor
  // de reducción cuando el marco es más estrecho que el mínimo del widget.
  const edubotFrame = useMemo(() => {
    const height = edubotHeight ?? selectedIframe?.height ?? 0;
    const scale =
      frameWidth > 0 && frameWidth < EDUBOT_MIN_WIDTH_PX
        ? frameWidth / EDUBOT_MIN_WIDTH_PX
        : 1;
    return { height, scale };
  }, [edubotHeight, frameWidth, selectedIframe]);

  const unedAuthUrl = useMemo(
    () =>
      buildUnedAuthUrl({
        returnPath: location.pathname,
        genreId: selectedGenreId,
      }),
    [location.pathname, selectedGenreId]
  );

  useEffect(() => {
    if (!selectedIframe) {
      clearAuthTimeout();
      setIsEdubotReady(false);
      setShowAuthNotice(false);
      return;
    }

    setEdubotHeight(null);
    startEdubotCheck();

    return () => {
      clearAuthTimeout();
    };
  }, [currentIframeClass]);

  useEffect(() => {
    const getEdubotIframe = (iframeId) => {
      if (!edubotIframeClasses.has(iframeId)) {
        return null;
      }

      return document.querySelector(`iframe.${iframeId}`);
    };

    const handleEdubotMessage = (e) => {
      const { data } = e;

      if (!data || typeof data !== "object") {
        return;
      }

      switch (data.type) {
        case "chatRedimensionado": {
          if (data.iframeid === currentIframeClass) {
            if (Number.isFinite(data.altura) && data.altura > 0) {
              setEdubotHeight(data.altura);
            }
            setIsEdubotReady(true);
            setShowAuthNotice(false);
            clearAuthTimeout();
          }
          break;
        }
        case "chatWidgetRespuesta":
          console.log("La IA ha respondido", data.formulario, data.respuesta);
          break;
        case "chatFinalizado":
          console.log("Chat finalizado");
          break;
        case "chatDisponible": {
          const iframe = getEdubotIframe(data.iframeid);

          if (data.iframeid === currentIframeClass) {
            setIsEdubotReady(true);
            setShowAuthNotice(false);
            clearAuthTimeout();
          }

          iframe?.contentWindow?.postMessage(
            {
              type: "testComunicacion",
              texto: "hola mundo",
            },
            "*"
          );
          break;
        }
        case "chatTestComunicacion":
          console.log(data.texto);
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleEdubotMessage);

    return () => {
      window.removeEventListener("message", handleEdubotMessage);
    };
  }, [currentIframeClass, edubotIframeClasses]);

  useEffect(
    () => () => {
      clearAuthTimeout();
    },
    []
  );

  // Al volver del login con Atrás, el navegador puede restaurar esta página tal
  // cual estaba (bfcache), con el aviso de "no autenticado" de antes. Remontar
  // el iframe recoge la sesión nueva sin obligar a pulsar "Ya me he autenticado".
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted && selectedIframe && !isEdubotReady) {
        retryEdubotCheck();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  });

  return (
    <div className="m-initialPage">
      <div className="m-title">{props.title}</div>
      <div className="m-selectors">
        <select
          className="m-genre-checker-select"
          onChange={(e) => setSelectedGenreId(e.target.value)}
          value={selectedGenreId}
        >
          <option value="" disabled>
            {globalT("Selecciona un género textual")}
          </option>
          {props.data.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
        {selectedIframe && (
          <div
            className={`m-genre-checker-iframe-wrapper ${
              isEdubotReady ? "" : "m-genre-checker-iframe-wrapper-notice"
            }`}
            ref={frameRef}
          >
            {!isEdubotReady && (
              <div className="m-genre-checker-auth-overlay">
                <div className="m-genre-checker-auth-card">
                  <p className="m-genre-checker-auth-title">
                    {showAuthNotice ? noticeCopy.info : noticeCopy.loading}
                  </p>
                  {showAuthNotice && (
                    <div className="m-genre-checker-auth-actions">
                      {/* El porqué de cada rama está en isEmbeddedUserAgent(). */}
                      <a
                        className="m-genre-checker-auth-link"
                        href={unedAuthUrl}
                        onClick={handleAuthLinkClick}
                        rel={embedded ? undefined : "noreferrer"}
                        target={embedded ? undefined : "_blank"}
                      >
                        {noticeCopy.loginButton}
                      </a>
                      <button
                        className="m-genre-checker-auth-button"
                        onClick={retryEdubotCheck}
                        type="button"
                      >
                        {noticeCopy.retryButton}
                      </button>
                      <button
                        className="m-genre-checker-auth-example"
                        onClick={() => setShowDemoPreview((v) => !v)}
                        type="button"
                      >
                        {noticeCopy.exampleButton}
                      </button>
                    </div>
                  )}
                  {/* Solo en WebKit. Allí las cookies de terceros están
                      bloqueadas: la sesión de la UNED no entra en el iframe y
                      "Ya me he autenticado" no puede desbloquear nada, por bien
                      que haya ido el login. La misma URL abierta como página
                      propia sí la ve, porque entonces es de primera parte.
                      En Chrome y en todo Android esto no se pinta: allí el
                      iframe funciona y no hay nada que arreglar. */}
                  {showAuthNotice && iframeSessionBlocked && selectedIframe.src && (
                    <div className="m-genre-checker-auth-webkit">
                      <p className="m-genre-checker-auth-opening">
                        {noticeCopy.webkitInfo}
                      </p>
                      <div className="m-genre-checker-auth-actions">
                        {/* Enlace puro, sin onClick: el diagnóstico de
                            handleAuthLinkClick es del SSO y aquí diría cosas
                            que no vienen a cuento. Pestaña nueva solo en un
                            navegador real; en la app, misma ventana, porque
                            allí una ventana nueva se traga el toque. */}
                        <a
                          className="m-genre-checker-auth-example"
                          href={selectedIframe.src}
                          rel={embedded ? undefined : "noreferrer"}
                          target={embedded ? undefined : "_blank"}
                        >
                          {noticeCopy.webkitButton}
                        </a>
                      </div>
                    </div>
                  )}
                  {showAuthNotice && authNavState === "opening" && (
                    <p className="m-genre-checker-auth-opening" role="status">
                      {noticeCopy.navOpening}
                    </p>
                  )}
                  {showAuthNotice && authNavState === "failed" && (
                    <div className="m-genre-checker-auth-failed" role="alert">
                      <p className="m-genre-checker-auth-failed-title">
                        {authFailureCopy()}
                      </p>
                      <p className="m-genre-checker-auth-failed-hint">
                        {noticeCopy.navFailedHint}
                      </p>
                      <button
                        className="m-genre-checker-auth-button"
                        onClick={retryAuthNavigation}
                        type="button"
                      >
                        {noticeCopy.navFailedRetry}
                      </button>
                      {clientLabel && (
                        <p className="m-genre-checker-auth-failed-client">
                          {clientLabel}
                        </p>
                      )}
                    </div>
                  )}
                  {showAuthNotice && showDemoPreview && (
                    <div className="m-genre-checker-demo">
                      <span className="m-genre-checker-demo-badge">
                        {noticeCopy.demoBadge}
                      </span>
                      <p className="m-genre-checker-demo-intro">
                        {noticeCopy.demoIntro}
                        {selectedGenre?.title ? ` (${selectedGenre.title})` : ""}
                      </p>
                      <div className="m-genre-checker-demo-block">
                        <div className="m-genre-checker-demo-label">
                          {noticeCopy.demoInputLabel}
                        </div>
                        <p className="m-genre-checker-demo-input">
                          {noticeCopy.demoInputText}
                        </p>
                      </div>
                      <div className="m-genre-checker-demo-block m-genre-checker-demo-feedback">
                        <div className="m-genre-checker-demo-label">
                          {noticeCopy.demoFeedbackLabel}
                        </div>
                        <p className="m-genre-checker-demo-output">
                          {noticeCopy.demoFeedbackText}
                        </p>
                      </div>
                      <p className="m-genre-checker-demo-note">
                        {noticeCopy.demoNote}
                      </p>
                      <button
                        className="m-genre-checker-auth-button"
                        onClick={() => setShowDemoPreview(false)}
                        type="button"
                      >
                        {noticeCopy.demoClose}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              className="m-genre-checker-iframe-viewport"
              style={{ height: `${edubotFrame.height * edubotFrame.scale}px` }}
            >
              <iframe
                allow={EDUBOT_IFRAME_ALLOW}
                className={`${selectedIframe.className} m-genre-checker-iframe ${
                  isEdubotReady ? "" : "m-genre-checker-iframe-hidden"
                }`}
                frameBorder="0"
                key={`${selectedIframe.className}-${iframeKey}`}
                onLoad={() => {
                  if (!isEdubotReady) {
                    armAuthNotice(EDUBOT_READY_AFTER_LOAD_MS);
                  }
                }}
                src={selectedIframe.src}
                style={{
                  height: `${edubotFrame.height}px`,
                  width: edubotFrame.scale < 1 ? `${EDUBOT_MIN_WIDTH_PX}px` : "100%",
                  transform: edubotFrame.scale < 1 ? `scale(${edubotFrame.scale})` : undefined,
                  transformOrigin: "top left",
                }}
                title={selectedIframe.title}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenreCheckerDropdownPage;
